import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { ModalShell, Pill } from '../lib/ui';

// Customer lookup for the Orders page: "has this person ordered before?",
// "who else is on this postcode?", "what does she usually get?". The matching
// lives on the server (garageFarmServer helpers/orderSearch.js) because it
// searches the whole order history, not just the window the page has loaded.

const API = 'https://teardrophydro.co.uk/garageFarm/orders/search';
const DEBOUNCE_MS = 250;

const muted      = { fontSize: 12, color: 'var(--muted)' };
const labelStyle = { fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };

// Things worth one click. Each is a valid query in its own right.
const EXAMPLES = [
  ['unfulfilled',  'Still to go out'],
  ['repeat',       'Customers who came back'],
  ['new',          'Bought exactly once'],
  ['cash',         'Cash orders'],
  ['last:30d',     'Ordered in the last 30 days'],
  ['over:25',      'Orders above £25'],
];

const SYNTAX = [
  ['jane', 'name, email, phone, address, or something they bought'],
  ['BS7', 'a postcode, or just the outward code for a whole round'],
  ['postcode:BS7 4NF', 'pin a word to one field: name, email, phone, postcode, address, city, item, note'],
  ['item:"pea shoots"', 'quote anything with a space in it'],
  ['jane unfulfilled', 'words stack up: all of them have to match'],
  ['last:6m over:20', 'time windows and amounts: last:30d, since:2026-01-01, over:20, under:5'],
];

const copy = (text) => { try { navigator.clipboard.writeText(text); } catch { /* clipboard blocked */ } };

function CopyButton({ text, title = 'Copy' }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  return (
    <button
      onClick={() => { copy(text); setDone(true); setTimeout(() => setDone(false), 1200); }}
      title={title}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
        color: done ? '#16a34a' : 'var(--muted)', fontSize: 11, fontWeight: 700,
      }}
    >{done ? 'copied' : 'copy'}</button>
  );
}

const formatAddress = (a) => [a?.line1, a?.line2, a?.city, a?.postcode].filter(Boolean).join(', ');

function AddressBlock({ address, isCurrent }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.45 }}>
      <div>{address.line1}</div>
      {address.line2 && <div>{address.line2}</div>}
      <div>{[address.city, address.postcode].filter(Boolean).join(', ')}</div>
      <div style={{ ...muted, marginTop: 2 }}>
        {isCurrent ? 'Current' : 'Previous'} · {address.orderCount} order{address.orderCount === 1 ? '' : 's'}
        <CopyButton text={formatAddress(address)} title="Copy address" />
      </div>
    </div>
  );
}

function OrderRow({ order, dimmed }) {
  const items = (order.items || []).filter((i) => i.name !== 'Local delivery');
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 0',
      borderTop: '1px solid var(--border)', fontSize: 12.5,
      opacity: dimmed ? 0.45 : 1,
    }}>
      <div style={{ width: 92, flexShrink: 0, color: 'var(--muted)' }}>
        {moment(order.timestamp).format('D MMM YY')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {items.length
          ? items.map((i) => `${i.name}${i.quantity > 1 ? ` ×${i.quantity}` : ''}`).join(', ')
          : <span style={muted}>No items listed</span>}
      </div>
      <div style={{ width: 58, textAlign: 'right', fontWeight: 600, flexShrink: 0 }}>
        £{Number(order.revenueAmount ?? order.total ?? 0).toFixed(2)}
      </div>
      <div style={{ width: 84, textAlign: 'right', flexShrink: 0, ...muted }}>
        {order.isSubscriptionOrder ? 'subscription' : (order.paymentMethod || 'card')}
      </div>
      <div style={{ width: 70, textAlign: 'right', flexShrink: 0 }}>
        {order.fulfilled
          ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Delivered</span>
          : <span style={{ color: '#b45309', fontWeight: 700 }}>To go out</span>}
      </div>
    </div>
  );
}

const COLLAPSED_ORDERS = 4;

function CustomerCard({ customer, onSearch }) {
  const [showAll, setShowAll] = useState(false);
  const orders  = showAll ? customer.orders : customer.orders.slice(0, COLLAPSED_ORDERS);
  const hidden  = customer.orders.length - orders.length;
  const sub     = customer.subscription;
  const returning = customer.buyingDays > 1;

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--r-xl)',
      background: 'var(--panel)', padding: '14px 16px', marginBottom: 12,
    }}>
      {/* Identity */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>{customer.name || 'Unnamed customer'}</span>
            {returning
              ? <Pill tone="ok">Ordered {customer.buyingDays} times</Pill>
              : <Pill tone="warn">First-time buyer</Pill>}
            {sub && (
              <Pill tone={sub.status === 'active' ? 'blue' : 'bad'}>
                {sub.status === 'active' ? `${sub.frequency} subscriber` : `subscription ${sub.status}`}
              </Pill>
            )}
          </div>
          <div style={{ ...muted, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {customer.email && (
              <span>
                <a href={`mailto:${customer.email}`} style={{ color: 'var(--brand-2)', textDecoration: 'none' }}>{customer.email}</a>
                <CopyButton text={customer.email} title="Copy email" />
              </span>
            )}
            {customer.phone && (
              <span>
                <a href={`tel:${customer.phone}`} style={{ color: 'var(--brand-2)', textDecoration: 'none' }}>{customer.phone}</a>
                <CopyButton text={customer.phone} title="Copy phone" />
              </span>
            )}
          </div>
        </div>

        {/* The relationship at a glance */}
        <div style={{ display: 'flex', gap: 18, flexShrink: 0 }}>
          {[
            ['Spent',      `£${customer.totalSpent.toFixed(2)}`],
            ['Avg order',  `£${customer.avgOrderValue.toFixed(2)}`],
            ['Last order', customer.lastOrder ? moment(customer.lastOrder).fromNow() : '-'],
            ['First order', customer.firstOrder ? moment(customer.firstOrder).format('MMM YY') : '-'],
          ].map(([label, value]) => (
            <div key={label} style={{ textAlign: 'right' }}>
              <div style={labelStyle}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Why this person came up */}
      {!!customer.matchReasons.length && (
        <div style={{ ...muted, marginTop: 8 }}>
          Matched on {customer.matchReasons.map((r, i) => (
            <span key={`${r.token}-${i}`}>
              {i > 0 && ' + '}
              <b style={{ color: 'var(--text)' }}>{r.label}</b>
              {r.value && r.field !== 'email' && r.field !== 'phone' ? ` (${r.value})` : ''}
            </span>
          ))}
          {customer.avgGapDays ? ` · usually reorders about every ${customer.avgGapDays} days` : ''}
        </div>
      )}

      {/* Addresses + favourites */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
        gap: 14, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)',
      }}>
        <div>
          <div style={labelStyle}>{customer.addresses.length > 1 ? 'Addresses used' : 'Address'}</div>
          <div style={{ display: 'grid', gap: 10, marginTop: 4 }}>
            {customer.addresses.length
              ? customer.addresses.map((a, i) => (
                  <AddressBlock key={`${a.postcode}-${i}`} address={a} isCurrent={i === 0} />
                ))
              : <span style={muted}>No address on file</span>}
          </div>
          {customer.addresses[0]?.postcode && (
            <button
              onClick={() => onSearch(`postcode:${customer.addresses[0].postcode.split(' ')[0]}`)}
              style={{
                marginTop: 6, background: 'var(--panel-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)', padding: '3px 8px', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', color: 'var(--muted)',
              }}
            >Who else is on this round?</button>
          )}
        </div>
        <div>
          <div style={labelStyle}>Usually buys</div>
          <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {customer.favourites.length
              ? customer.favourites.map((f) => (
                  <span key={f.name} style={{
                    background: 'var(--panel-2)', border: '1px solid var(--border)',
                    borderRadius: 999, padding: '2px 9px', fontSize: 12,
                  }}>{f.name} <b>×{f.qty}</b></span>
                ))
              : <span style={muted}>Nothing recorded</span>}
          </div>
        </div>
      </div>

      {/* History */}
      <div style={{ marginTop: 12 }}>
        <div style={labelStyle}>
          Order history ({customer.orderCount})
          {customer.matchedOrderCount < customer.orderCount && (
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
              {' '}· {customer.matchedOrderCount} match the filters
            </span>
          )}
        </div>
        <div style={{ marginTop: 4 }}>
          {orders.map((o) => (
            <OrderRow
              key={o._id}
              order={o}
              dimmed={customer.matchedOrderCount < customer.orderCount && !o.matched}
            />
          ))}
        </div>
        {hidden > 0 && (
          <button
            onClick={() => setShowAll(true)}
            style={{
              marginTop: 6, background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--brand-2)', fontSize: 12, fontWeight: 700, padding: 0,
            }}
          >Show {hidden} older order{hidden === 1 ? '' : 's'}</button>
        )}
      </div>
    </div>
  );
}

export default function OrderSearchModal({ onClose, initialQuery = '' }) {
  const [query, setQuery]     = useState(initialQuery);
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const inputRef = useRef(null);
  // Guards against a slow early request landing after a faster later one and
  // repainting the list with stale results.
  const requestSeq = useRef(0);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const runSearch = useCallback(async (q) => {
    const seq = ++requestSeq.current;
    if (!q.trim()) { setResult(null); setLoading(false); setError(''); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(API, {
        params: { q, limit: 40 },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (seq !== requestSeq.current) return;
      setResult(res.data);
      setError('');
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('Order search failed', err);
      setError('Search failed. Check the connection and try again.');
      setResult(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const customers = useMemo(() => result?.customers || [], [result]);
  const totals = useMemo(() => ({
    people: result?.totalMatched || 0,
    orders: customers.reduce((s, c) => s + c.orderCount, 0),
    spent:  customers.reduce((s, c) => s + c.totalSpent, 0),
  }), [result, customers]);

  return (
    <ModalShell
      onClose={onClose} width={1120} maxWidth="96vw" ariaLabel="Search orders"
      panelStyle={{ padding: '20px 24px 18px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Find a customer</h2>
          <p style={{ ...muted, margin: '3px 0 0' }}>
            Searches every order ever placed, not just the window on the page behind this.
          </p>
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
          width: 30, height: 30, cursor: 'pointer', fontSize: 16, color: 'var(--muted)', flexShrink: 0,
        }}>✕</button>
      </div>

      {/* Search box */}
      <div style={{ position: 'relative', margin: '14px 0 10px' }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, opacity: 0.5 }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, email, phone, address, postcode, or something they bought"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '11px 88px 11px 36px',
            border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
            background: 'var(--panel)', color: 'var(--text)', fontSize: 15, outline: 'none',
          }}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
              padding: '3px 9px', fontSize: 11, fontWeight: 700, color: 'var(--muted)', cursor: 'pointer',
            }}
          >Clear</button>
        )}
      </div>

      {/* Quick filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {EXAMPLES.map(([q, hint]) => (
          <button
            key={q} title={hint}
            onClick={() => setQuery((prev) => (prev.includes(q) ? prev : `${prev} ${q}`.trim()))}
            style={{
              background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 999,
              padding: '3px 11px', fontSize: 12, fontWeight: 600, color: 'var(--muted)', cursor: 'pointer',
            }}
          >{q}</button>
        ))}
      </div>

      {/* What the query was taken to mean */}
      {result?.query?.interpreted?.length > 0 && (
        <div style={{ ...muted, marginBottom: 10 }}>
          Reading that as: {result.query.interpreted.join(' · ')}
        </div>
      )}

      {error && <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}

      {/* Results */}
      {!query.trim() && (
        <div style={{
          border: '1px dashed var(--border)', borderRadius: 'var(--r-xl)',
          padding: '16px 18px', background: 'var(--panel-2)',
        }}>
          <div style={labelStyle}>How to search</div>
          <div style={{ display: 'grid', gap: 7, marginTop: 8 }}>
            {SYNTAX.map(([example, hint]) => (
              <div key={example} style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <code style={{
                  background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
                  padding: '2px 7px', fontSize: 12, whiteSpace: 'nowrap',
                }}>{example}</code>
                <span style={muted}>{hint}</span>
              </div>
            ))}
          </div>
          <div style={{ ...muted, marginTop: 10 }}>
            Near-misses count: a typo in a name still finds them.
          </div>
        </div>
      )}

      {query.trim() && result && (
        <>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
            padding: '8px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
            marginBottom: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {totals.people === 0
                ? 'No one matches that'
                : `${totals.people} ${totals.people === 1 ? 'customer' : 'customers'}`}
              {totals.people > 0 && (
                <span style={{ ...muted, fontWeight: 400 }}>
                  {' '}· {totals.orders} orders shown · £{totals.spent.toFixed(2)} lifetime
                </span>
              )}
            </div>
            {loading && <span style={muted}>Searching…</span>}
          </div>

          {totals.people > customers.length && (
            <div style={{ ...muted, marginBottom: 10 }}>
              Showing the {customers.length} closest matches. Add another word to narrow it down.
            </div>
          )}

          {customers.map((c) => (
            <CustomerCard key={c.key} customer={c} onSearch={setQuery} />
          ))}

          {totals.people === 0 && !loading && (
            <div style={{ padding: '30px 0', textAlign: 'center', ...muted }}>
              Nothing found. Try fewer words, or just the postcode.
            </div>
          )}
        </>
      )}

      {query.trim() && !result && loading && (
        <div style={{ padding: '30px 0', textAlign: 'center', ...muted }}>Searching…</div>
      )}
    </ModalShell>
  );
}
